import { useState } from 'react';

interface RazorpayOptions {
  key: string;
  amount: number;
  currency: string;
  name: string;
  description: string;
  order_id: string;
  handler: (response: RazorpayResponse) => void;
  prefill: {
    name: string;
    email: string;
    contact: string;
  };
  theme: {
    color: string;
  };
  modal: {
    ondismiss: () => void;
  };
}

interface RazorpayResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

interface CreateOrderData {
  amount: number;
  currency: string;
  receipt: string;
  customer_info?: {
    name: string;
    email: string;
    phone: string;
  };
}

interface VerifyPaymentData {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
  order_details: {
    customer_id?: string;
    customer_name: string;
    customer_email: string;
    customer_phone: string;
    shipping_address: any;
    cart_items: Array<{
      product_id: string;
      name: string;
      price: number;
      quantity: number;
      image_url?: string;
    }>;
    shipping_cost: number;
    total_amount: number;
  };
}

// Declare Razorpay on window object
declare global {
  interface Window {
    Razorpay: any;
  }
}

export const useRazorpay = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createOrder = async (orderData: CreateOrderData) => {
    setLoading(true);
    setError(null);

    try {
      console.log('Creating Razorpay order:', orderData);

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/razorpay-orders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'create_order',
          ...orderData,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Create order error:', errorData);
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Order created successfully:', data);
      
      if (data.error) {
        throw new Error(data.message || data.error);
      }

      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create order';
      setError(errorMessage);
      console.error('Create order error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const verifyPayment = async (paymentData: VerifyPaymentData) => {
    setLoading(true);
    setError(null);

    try {
      console.log('Verifying payment:', {
        razorpay_order_id: paymentData.razorpay_order_id,
        razorpay_payment_id: paymentData.razorpay_payment_id,
      });

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/razorpay-orders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'verify_payment',
          ...paymentData,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Verify payment error:', errorData);
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Payment verified successfully:', data);
      
      if (data.error) {
        throw new Error(data.message || data.error);
      }

      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to verify payment';
      setError(errorMessage);
      console.error('Verify payment error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const loadRazorpayScript = (): Promise<boolean> => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  const openRazorpay = async (options: RazorpayOptions): Promise<void> => {
    const scriptLoaded = await loadRazorpayScript();
    
    if (!scriptLoaded) {
      throw new Error('Failed to load Razorpay script');
    }
    
    // Validate required options
    if (!options.key) {
      throw new Error('Razorpay key is required');
    }
    
    if (!options.order_id) {
      throw new Error('Razorpay order ID is required');
    }
    
    if (options.amount < 100) {
      throw new Error('Amount must be at least ₹1 (100 paise)');
    }

    return new Promise((resolve, reject) => {
      const rzp = new window.Razorpay({
        ...options,
        handler: (response: RazorpayResponse) => {
          console.log('Razorpay payment response:', response);
          options.handler(response);
          resolve();
        },
        modal: {
          ...options.modal,
          ondismiss: () => {
            console.log('Razorpay modal dismissed');
            options.modal.ondismiss();
            reject(new Error('Payment was cancelled'));
          },
        },
      });

      try {
        rzp.open();
      } catch (error) {
        console.error('Error opening Razorpay:', error);
        reject(new Error('Failed to open payment window'));
      }
    });
  };

  return {
    createOrder,
    verifyPayment,
    openRazorpay,
    loading,
    error,
  };
};